import { GoogleGenerativeAI } from '@google/generative-ai';

// 환경변수에서 API 키 가져오기
const GEMINI_API_KEY = process.env.REACT_APP_GEMINI_API_KEY;

// 모델 상수
export const GEMINI_MODELS = {
  TEXT: 'gemini-3-flash-preview', // 일반 대화용
  IMAGE: 'gemini-3.1-flash-image-preview', // 이미지 생성용
};

// 이미지 옵션 상수
export const IMAGE_OPTIONS = {
  ASPECT_RATIOS: ['1:1', '2:3', '3:2', '3:4', '4:3', '4:5', '5:4', '9:16', '16:9', '21:9'],
  IMAGE_SIZES: ['1K', '2K', '4K'],
  DEFAULTS: {
    aspectRatio: '16:9',
    imageSize: '1K',
  },
};

// GoogleGenerativeAI 인스턴스 생성
let genAI = null;

const getGenAI = () => {
  if (!GEMINI_API_KEY) {
    throw new Error('REACT_APP_GEMINI_API_KEY 환경변수가 설정되지 않았습니다.');
  }
  if (!genAI) {
    genAI = new GoogleGenerativeAI(GEMINI_API_KEY);
  }
  return genAI;
};

/**
 * 채팅 히스토리를 Gemini API 형식으로 변환
 * @param {Array} messages - ChatContext의 messages 배열
 * @returns {Array} - Gemini API 형식의 히스토리
 */
const convertToGeminiHistory = (messages) => {
  return messages
    .filter((msg) => !msg.isError) // 에러 메시지 제외
    .map((msg) => ({
      role: msg.role === 'user' ? 'user' : 'model',
      parts: [{ text: msg.content }],
    }));
};

/**
 * 일반 텍스트 채팅 요청
 * @param {string} message - 사용자 메시지
 * @param {Array} history - 이전 대화 히스토리
 * @param {boolean} webSearch - 웹 검색 그라운딩 사용 여부
 * @returns {Promise<{content: string, images: Array, groundingMetadata: Object|null}>}
 */
export const sendTextMessage = async (message, history = [], webSearch = false) => {
  const ai = getGenAI();

  // 모델 설정 (웹 검색 그라운딩 포함)
  const modelConfig = {
    model: GEMINI_MODELS.TEXT,
  };

  // 웹 검색 도구 설정
  if (webSearch) {
    modelConfig.tools = [{ googleSearch: {} }];
  }

  const model = ai.getGenerativeModel(modelConfig);

  let result;
  // 히스토리가 있으면 채팅 모드, 없으면 단일 요청
  if (history.length > 0) {
    const chatConfig = {
      history: convertToGeminiHistory(history),
    };

    const chat = model.startChat(chatConfig);
    result = await chat.sendMessage(message);
  } else {
    result = await model.generateContent(message);
  }

  const response = await result.response;
  const text = response.text();

  // 그라운딩 메타데이터 추출 (웹 검색 결과 포함)
  let groundingMetadata = null;
  if (webSearch && response.candidates?.[0]?.groundingMetadata) {
    groundingMetadata = response.candidates[0].groundingMetadata;
  }

  return {
    content: text,
    images: [],
    groundingMetadata,
  };
};

/**
 * 이미지 생성 채팅 요청
 * @param {string} message - 사용자 메시지
 * @param {Array} history - 이전 대화 히스토리
 * @param {Object} imageOptions - 이미지 생성 옵션
 * @param {string} imageOptions.aspectRatio - 이미지 비율 (기본값: '16:9')
 * @param {string} imageOptions.imageSize - 이미지 해상도 (기본값: '1K')
 * @returns {Promise<{content: string, images: Array}>}
 */
export const sendImageMessage = async (message, history = [], imageOptions = {}) => {
  const ai = getGenAI();

  const { aspectRatio = IMAGE_OPTIONS.DEFAULTS.aspectRatio, imageSize = IMAGE_OPTIONS.DEFAULTS.imageSize } = imageOptions;

  // 이미지 생성 모델 설정 (imageConfig를 generationConfig 내에 포함)
  const generationConfig = {
    responseModalities: ['text', 'image'],
    // imageConfig로 비율과 해상도 설정
    imageConfig: {
      aspectRatio: aspectRatio,
      imageSize: imageSize,
    },
  };

  const model = ai.getGenerativeModel({
    model: GEMINI_MODELS.IMAGE,
    generationConfig,
  });

  // 이미지 생성 요청
  const result = await model.generateContent(message);
  const response = await result.response;

  const images = [];
  let textContent = '';

  // 응답에서 텍스트와 이미지 분리
  for (const candidate of response.candidates || []) {
    for (const part of candidate.content?.parts || []) {
      if (part.text) {
        textContent += part.text;
      } else if (part.inlineData) {
        // 이미지 데이터를 base64 URL로 변환
        const imageId = `img_${Date.now()}_${images.length}`;
        const mimeType = part.inlineData.mimeType || 'image/png';
        const base64Data = part.inlineData.data;
        const imageSrc = `data:${mimeType};base64,${base64Data}`;

        images.push({
          id: imageId,
          src: imageSrc,
          alt: '생성된 이미지',
        });
      }
    }
  }

  // 텍스트가 없으면 기본 메시지
  if (!textContent.trim()) {
    textContent = images.length > 0 ? `${images.length}개의 이미지를 생성했습니다.` : '응답을 생성할 수 없습니다.';
  }

  return {
    content: textContent,
    images,
  };
};

/**
 * 통합 메시지 전송 함수
 * @param {string} message - 사용자 메시지
 * @param {Array} history - 이전 대화 히스토리
 * @param {boolean} generateImage - 이미지 생성 모드 여부
 * @param {Object} imageOptions - 이미지 생성 옵션 (이미지 모드일 때만 사용)
 * @param {boolean} webSearch - 웹 검색 그라운딩 사용 여부 (텍스트 모드일 때만 사용)
 * @returns {Promise<{content: string, images: Array, groundingMetadata?: Object}>}
 */
export const sendGeminiMessage = async (message, history = [], generateImage = false, imageOptions = {}, webSearch = false) => {
  if (generateImage) {
    return sendImageMessage(message, history, imageOptions);
  } else {
    return sendTextMessage(message, history, webSearch);
  }
};

/**
 * API 키 유효성 확인
 * @returns {boolean}
 */
export const isGeminiConfigured = () => {
  return !!GEMINI_API_KEY;
};

export default {
  sendTextMessage,
  sendImageMessage,
  sendGeminiMessage,
  isGeminiConfigured,
  GEMINI_MODELS,
  IMAGE_OPTIONS,
};
